import { RefreshCw } from 'lucide-react'
import { ButtonGlyph, buttonClass, type ButtonSize } from './Button.js'

function SpinningRefreshIcon({ className }: { className?: string }) {
  return <RefreshCw className={`${className ?? ''} animate-spin`} />
}

/** Square padding per size, so the icon-only button is as tall as a labelled one beside it. */
const ICON_ONLY_PADDING: Record<ButtonSize, string> = {
  sm: '!px-1.5',
  md: '!px-2',
  lg: '!px-2.5',
}

interface RefreshButtonProps {
  /** Re-reads what the list shows; the button waits on it. */
  onRefresh: () => unknown
  /** True while the list is being read, so the icon spins and a second press waits. */
  refreshing: boolean
  size?: ButtonSize
  className?: string
}

/**
 * The Refresh button that sits beside every list a record can be created in,
 * so a record someone else just added (another tab, a colleague, a partner's
 * system) shows without reloading the page. Outlined and icon only, beside the
 * New button; its name is in the tooltip and for screen readers.
 */
export function RefreshButton({ onRefresh, refreshing, size = 'md', className = '' }: RefreshButtonProps) {
  const label = refreshing ? 'Refreshing…' : 'Refresh'
  return (
    <button
      type="button"
      className={buttonClass({ variant: 'secondary', size, className: `${ICON_ONLY_PADDING[size]} ${className}` })}
      disabled={refreshing}
      onClick={() => void onRefresh()}
      title={label}
      aria-label={label}
    >
      <ButtonGlyph icon={refreshing ? SpinningRefreshIcon : RefreshCw} size={size} />
    </button>
  )
}
